import { icon } from "./icons";

export interface HomeScreenOptions {
  readonly mascotUrl: string;
  readonly coins: number;
  readonly energy: number;
  readonly onStart: () => void;
}

function drawPreview(canvas: HTMLCanvasElement): void {
  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }
  const density = Math.min(window.devicePixelRatio || 1, 2);
  const size = Math.max(260, Math.round(canvas.clientWidth * density));
  canvas.width = size;
  canvas.height = size;
  context.scale(size / 280, size / 280);
  context.fillStyle = "#d2d1cf";
  context.fillRect(28, 28, 224, 224);
  const cell = 16;
  for (let y = 0; y < 14; y += 1) {
    for (let x = 0; x < 14; x += 1) {
      const dx = x - 6.5;
      const dy = y - 6.5;
      const body = dx * dx * 0.8 + dy * dy < 29;
      const ears = (y < 4 && (x < 5 || x > 8)) || (y === 4 && (x === 4 || x === 9));
      const eye = y === 7 && (x === 5 || x === 8);
      const smile = y === 9 && x >= 5 && x <= 8;
      if (body || ears) {
        context.fillStyle = eye ? "#343536" : smile ? "#f5f4ef" : "#737577";
        context.fillRect(28 + x * cell, 28 + y * cell, cell, cell);
      }
    }
  }
  context.strokeStyle = "rgba(85,82,91,.28)";
  context.lineWidth = 2;
  context.strokeRect(28, 28, 224, 224);
}

export function createHomeScreen(
  host: HTMLElement,
  options: HomeScreenOptions,
): void {
  host.innerHTML = `
    <section class="screen home-screen" data-screen="home">
      <header class="home-hud">
        <img class="mascot-avatar" src="${options.mascotUrl}" alt="豆铺精灵" />
        <div class="resource-pill coin-pill">${icon("coin")}<strong>${options.coins}</strong></div>
        <div class="resource-pill energy-pill">${icon("heart")}<strong>${options.energy}</strong><span>已满</span><i>+</i></div>
        <button class="plain-icon-button home-settings" type="button" aria-label="设置">${icon("gear")}</button>
      </header>

      <div class="home-stars" aria-hidden="true"><i></i><i></i><i></i><i></i></div>
      <main class="home-main">
        <div class="level-chip">第14关</div>
        <div class="preview-machine" aria-label="本关图案预览">
          <div class="machine-handle"></div>
          <canvas class="preview-canvas"></canvas>
        </div>
      </main>

      <nav class="home-shortcuts" aria-label="主页功能">
        <button type="button" data-meta="签到"><span class="shortcut-art calendar-art"><b>7</b></span><strong>七日签到</strong></button>
        <button type="button" data-meta="排行"><span class="shortcut-art cup-art"></span><strong>排行榜</strong></button>
        <button type="button" data-meta="图鉴"><span class="shortcut-art book-art"></span><strong>图鉴</strong></button>
      </nav>

      <footer class="home-actions">
        <button class="daily-button" type="button" data-meta="每日一关"><span>每日一关</span><small>完成普通关后解锁</small></button>
        <button class="start-button" type="button" data-action="start">
          <span>开始游戏</span><small>${icon("heart")} -1</small>
        </button>
      </footer>
      <div class="toast" role="status" aria-live="polite"></div>
    </section>
  `;

  const preview = host.querySelector<HTMLCanvasElement>(".preview-canvas");
  if (preview) {
    window.requestAnimationFrame(() => drawPreview(preview));
  }

  host.querySelector<HTMLButtonElement>("[data-action='start']")?.addEventListener("click", options.onStart);
  host.querySelectorAll<HTMLButtonElement>("[data-meta]").forEach((button) => {
    button.addEventListener("click", () => {
      const label = button.dataset.meta ?? "该入口";
      showHomeToast(host, `${label}已记录；当前试玩先完整还原核心关卡`);
    });
  });
  host.querySelector<HTMLButtonElement>(".home-settings")?.addEventListener("click", () => {
    showHomeToast(host, "音效、音乐与震动保持录屏中的关闭状态");
  });
}

function showHomeToast(host: HTMLElement, message: string): void {
  const toast = host.querySelector<HTMLElement>(".toast");
  if (!toast) {
    return;
  }
  toast.textContent = message;
  toast.classList.add("is-visible");
  window.setTimeout(() => toast.classList.remove("is-visible"), 2100);
}
